<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import wootConstants from 'dashboard/constants/globals';

import InboxCard from 'dashboard/components-next/Inbox/InboxCard.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'next/icon/Icon.vue';

const emit = defineEmits(['close']);

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const activeTab = ref('new');

const meta = useMapGetter('notifications/getMeta');
const uiFlags = useMapGetter('notifications/getUIFlags');
const records = useMapGetter('notifications/getFilteredNotificationsV4');
const inboxById = useMapGetter('inboxes/getInboxById');

const tabs = [
  { key: 'new', label: t('SIDEBAR.NOTIFICATIONS_PANEL.NEW') },
  { key: 'archived', label: t('SIDEBAR.NOTIFICATIONS_PANEL.ARCHIVED') },
  { key: 'all', label: t('SIDEBAR.NOTIFICATIONS_PANEL.ALL') },
];

const filters = computed(() => {
  if (activeTab.value === 'archived') {
    return {
      page: 1,
      type: wootConstants.INBOX_DISPLAY_BY.READ,
      sortOrder: wootConstants.INBOX_SORT_BY.NEWEST,
    };
  }

  return {
    page: 1,
    sortOrder: wootConstants.INBOX_SORT_BY.NEWEST,
  };
});

const notifications = computed(() => {
  const allRecords = records.value(filters.value);
  if (activeTab.value === 'new') return allRecords.filter(item => !item.readAt);
  return allRecords;
});

const stateInbox = inboxId => inboxById.value(inboxId);

const fetchNotifications = () => {
  store.dispatch('notifications/clear');
  store.dispatch('notifications/index', filters.value);
};

const selectTab = tab => {
  activeTab.value = tab;
  fetchNotifications();
};

const archiveAll = async () => {
  await store.dispatch('notifications/readAll');
  await store.dispatch('notifications/unReadCount');
  useAlert(t('INBOX.ALERTS.MARK_ALL_READ'));
  fetchNotifications();
};

const openConversation = async notificationItem => {
  const {
    id,
    primaryActorId,
    primaryActorType,
    primaryActor: { id: conversationId },
  } = notificationItem;

  await store.dispatch('notifications/read', {
    id,
    primaryActorId,
    primaryActorType,
    unreadCount: meta.value.unreadCount,
  });
  await store.dispatch('notifications/unReadCount');
  emit('close');
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: conversationId },
  });
};

onMounted(fetchNotifications);
</script>

<template>
  <section
    class="absolute z-50 flex flex-col w-[28rem] h-[40rem] max-h-[calc(100vh-4rem)] overflow-hidden rounded-lg shadow-xl ltr:left-[47px] rtl:right-[47px] bottom-8 bg-n-solid-1 border border-n-weak"
  >
    <header class="flex items-center justify-between px-4 pt-4 pb-2">
      <h2 class="m-0 text-base font-medium text-n-slate-12">
        {{ t('SIDEBAR.NOTIFICATIONS') }}
      </h2>
      <button
        type="button"
        class="text-sm text-n-slate-12 hover:text-n-brand"
        @click="archiveAll"
      >
        {{ t('SIDEBAR.NOTIFICATIONS_PANEL.ARCHIVE_ALL') }}
      </button>
    </header>
    <div class="flex gap-4 px-4 border-b border-n-weak">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="px-0 py-3 text-sm font-medium border-b-2"
        :class="
          activeTab === tab.key
            ? 'text-n-brand border-n-brand'
            : 'text-n-slate-11 border-transparent hover:text-n-slate-12'
        "
        @click="selectTab(tab.key)"
      >
        {{ tab.label }}
      </button>
    </div>
    <div class="flex-1 min-h-0 overflow-y-auto">
      <div v-if="uiFlags.isFetching" class="grid h-full place-content-center">
        <Spinner class="text-n-brand" />
      </div>
      <div
        v-else-if="!notifications.length"
        class="grid h-full gap-4 place-content-center text-center"
      >
        <Icon icon="i-lucide-inbox" class="mx-auto size-5 text-n-brand" />
        <div>
          <p class="mb-1 text-base font-medium text-n-slate-12">
            {{ t('SIDEBAR.NOTIFICATIONS_PANEL.EMPTY_TITLE') }}
          </p>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('SIDEBAR.NOTIFICATIONS_PANEL.EMPTY_DESCRIPTION') }}
          </p>
        </div>
      </div>
      <div v-else class="px-2 py-2 divide-y divide-n-weak">
        <InboxCard
          v-for="notificationItem in notifications"
          :key="notificationItem.id"
          :inbox-item="notificationItem"
          :state-inbox="stateInbox(notificationItem.primaryActor?.inboxId)"
          class="rounded-none hover:rounded-lg hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3"
          @click="openConversation(notificationItem)"
        />
      </div>
    </div>
  </section>
</template>
